import { inject, Injectable } from '@angular/core';
import { UserVacationV2 } from '@wilson/absences';
import { firstValueFrom } from 'rxjs';
import { VacationManagementV2Service } from '../../vacation-management-v2.service';

@Injectable()
export class VacationManagementV2BatchingService {
  private readonly HYDRATE_BATCH_SIZE = 50;
  readonly MAX_PARALLEL_BATCHES = 4;

  private readonly vacationManagementV2Service = inject(
    VacationManagementV2Service,
  );

  private readonly vacationCache = new Map<string, UserVacationV2>();

  private cacheKey(userId: string, year: number): string {
    return `${userId}:${year}`;
  }

  getCachedVacation(userId: string, year: number): UserVacationV2 | undefined {
    return this.vacationCache.get(this.cacheKey(userId, year));
  }

  setCachedVacations(vacations: UserVacationV2[], year: number): void {
    vacations.forEach((vacation) => {
      this.vacationCache.set(this.cacheKey(vacation.userId, year), vacation);
    });
  }

  clearCache(): void {
    this.vacationCache.clear();
  }

  getIdsToFetch(vacations: UserVacationV2[], year: number): string[] {
    return [
      ...new Set(
        vacations
          .filter(
            (v) => v.id == null && !this.getCachedVacation(v.userId, year),
          )
          .map((v) => v.userId),
      ),
    ];
  }

  createBatches(vacations: UserVacationV2[], year: number): string[][] {
    const userIdsToFetch = this.getIdsToFetch(vacations, year);
    const batches: string[][] = [];
    for (let i = 0; i < userIdsToFetch.length; i += this.HYDRATE_BATCH_SIZE) {
      batches.push(userIdsToFetch.slice(i, i + this.HYDRATE_BATCH_SIZE));
    }
    return batches;
  }

  async fetchAndCacheBatch(userIds: string[], year: number): Promise<void> {
    const fetched = await firstValueFrom(
      this.vacationManagementV2Service.getUsersVacationStatus({
        where: { year, userIds },
      }),
    );
    this.setCachedVacations(fetched, year);
  }

  // TO-DO: Pls delete this trash when we have a proper bulk endpoint
  async processInBatches<T>(
    items: T[],
    batchSize: number,
    processFn: (item: T) => Promise<void>,
    onItemDone?: () => void,
  ): Promise<void> {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (item) => {
          await processFn(item);
          onItemDone?.();
        }),
      );
    }
  }
}
